/**
 * Shared filter vocabulary handling for table pages: a per-table schema describes each filter key
 * once, and URL parsing, URL writing, chip labels and the "is anything filtered" check derive from
 * it. Filter state always lives in the URL (shareable, survives reload, back button works).
 *
 * Schema entry: { type: 'string'|'bool'|'yesNo'|'number'|'enum'|'enumList'|'idList', label, options?, formatValue? }
 * - string: free text (search); empty = inactive
 * - bool: active only when true
 * - yesNo: true or false filters (has / has not), unset = either
 * - number: finite, non-negative number
 * - enum: one value from `options` [{ value, label }]
 * - enumList: several values from `options`, OR-combined by the backend
 * - idList: several ids whose options load at runtime (tags, owners); chips need `formatValue`
 * `formatValue(value)` overrides the chip text after the label (e.g. currency).
 */

const PARSERS = {
	string: (raw) => {
		const value = raw.trim();
		return value === '' ? undefined : value;
	},
	bool: (raw) => (raw === 'true' ? true : undefined),
	yesNo: (raw) => (raw === 'true' || raw === 'false' ? raw === 'true' : undefined),
	number: (raw) => {
		const value = Number.parseFloat(raw);
		return Number.isFinite(value) && value >= 0 ? value : undefined;
	},
	enum: (raw, spec) => (spec.options.some((option) => option.value === raw) ? raw : undefined),
	enumList: (raw, spec) => {
		const allowed = new Set(spec.options.map((option) => option.value));
		const values = raw.split(',').filter((value) => allowed.has(value));
		return values.length > 0 ? values : undefined;
	},
	idList: (raw) => {
		const values = raw.split(',').filter(Boolean);
		return values.length > 0 ? values : undefined;
	},
};

const parseValue = (spec, raw) => (raw === null || raw === undefined ? undefined : PARSERS[spec.type]?.(raw, spec));

/**
 * Only set and valid keys, in schema order — so JSON.stringify of the result is a stable key.
 *
 * @param {URLSearchParams} searchParams
 * @param {Record<string, object>} schema
 * @param {string} [prefix=''] - URL param prefix (several tables on one page)
 * @returns {Object}
 */
export const parseTableFilters = (searchParams, schema, prefix = '') => {
	const filters = {};
	for (const [key, spec] of Object.entries(schema)) {
		const value = parseValue(spec, searchParams.get(`${prefix}${key}`));
		if (value !== undefined) filters[key] = value;
	}
	return filters;
};

/**
 * URL form of one filter value; null = remove the param.
 *
 * @param {*} value
 * @returns {string|null}
 */
export const serializeFilterValue = (value) => {
	if (value === undefined || value === null || value === '') return null;
	if (Array.isArray(value)) return value.length > 0 ? value.join(',') : null;
	return String(value);
};

/**
 * @param {Object} filters - Result of parseTableFilters
 * @returns {boolean}
 */
export const hasActiveFilters = (filters) => Object.keys(filters).length > 0;

/**
 * Removable chip per active filter, labelled from the schema.
 *
 * @param {Object} filters - Result of parseTableFilters
 * @param {Record<string, object>} schema
 * @returns {Array<{id: string, label: string, paramKeys: string[]}>}
 */
export const buildFilterChips = (filters, schema) =>
	Object.entries(filters).map(([key, value]) => {
		const spec = schema[key];
		const labelOf = new Map((spec.options ?? []).map((option) => [option.value, option.label]));
		let text;
		if (spec.formatValue) text = `${spec.label}: ${spec.formatValue(value)}`;
		else if (spec.type === 'bool') text = spec.label;
		else if (spec.type === 'yesNo') text = `${spec.label}: ${value ? 'Ja' : 'Nein'}`;
		else if (spec.type === 'enumList')
			text = `${spec.label}: ${value.map((entry) => labelOf.get(entry) ?? entry).join(', ')}`;
		else if (spec.type === 'idList') text = `${spec.label}: ${value.length} ausgewählt`;
		else if (spec.type === 'enum') text = `${spec.label}: ${labelOf.get(value) ?? value}`;
		else if (spec.type === 'number') text = `${spec.label}: ${value.toLocaleString('de-DE')}`;
		else text = `${spec.label}: „${value}“`;
		return { id: key, label: text, paramKeys: [key] };
	});

/**
 * Query-string params of the active filters for list endpoints (enum lists comma-joined).
 *
 * @param {Object} filters
 * @returns {Object<string, string>}
 */
export const filtersToQueryParams = (filters) =>
	Object.fromEntries(
		Object.entries(filters)
			.map(([key, value]) => [key, serializeFilterValue(value)])
			.filter(([, value]) => value !== null),
	);
